import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { customerFormsApi } from "../../shared/customerFormsApi";
import { formTypeLabel, today } from "../../shared/customerForms";
import { fromDateOnlyString } from "../../shared/dateOnly";
import type { FormOptions, FormSubmission, FormType } from "../../types/forms";
import {
  buttonClass,
  Field,
  FormInput,
  FormSelect,
  inputClass,
  secondaryClass,
} from "./FormControls";

interface Props {
  options: FormOptions;
  customerId?: number;
  initialType?: FormType;
  returnTo: string;
  onClose: () => void;
  onBusy: (busy: boolean) => void;
}
export default function NewCustomerForm({
  options,
  customerId,
  initialType = "credit",
  returnTo,
  onClose,
  onBusy,
}: Props) {
  const navigate = useNavigate();
  const [type, setType] = useState<FormType>(initialType);
  const [customer, setCustomer] = useState(String(customerId || ""));
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const submission = useRef<{ signature: string; id: string } | undefined>(undefined);
  const locked = useRef(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (locked.current) return;
    const invalid: Record<string, string[]> = {};
    if (!customer) invalid.customer_id = ["Select a customer."];
    if (!title.trim() || title.trim().length > (type === "credit" ? 100 : 255))
      invalid.title = [
        "Enter a valid " +
          (type === "credit"
            ? "invoice number (up to 100 characters)."
            : "event name (up to 255 characters)."),
      ];
    if (!description.trim() || description.trim().length > 10000)
      invalid.description = ["Enter a description of up to 10,000 characters."];
    if (type === "demo" && (!fromDateOnlyString(date) || date < today()))
      invalid.event_date = ["Select today or a future date."];
    setErrors(invalid);
    if (Object.keys(invalid).length) return;
    const body =
      type === "credit"
        ? { invoice_number: title.trim(), description: description.trim() }
        : { event_name: title.trim(), event_date: date, description: description.trim() };
    const signature = JSON.stringify([type, customer, body]);
    if (submission.current?.signature !== signature)
      submission.current = { signature, id: crypto.randomUUID() };
    locked.current = true;
    setSaving(true);
    onBusy(true);
    setError("");
    const result = await customerFormsApi.submit(type, Number(customer), {
      ...body,
      request_id: submission.current.id,
    } as FormSubmission);
    locked.current = false;
    setSaving(false);
    onBusy(false);
    if (result.success)
      navigate(`/forms/${type}/${result.data.id}`, { state: { returnTo, submitted: true } });
    else {
      setError(result.data.message || "Could not submit. Check your connection and retry.");
      setErrors(result.data.errors || {});
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      <h2 className="text-xl font-semibold">New {formTypeLabel(type)}</h2>
      <fieldset disabled={saving} className="space-y-4">
        <Field label="Form type">
          <FormSelect
            value={type}
            onChange={(e) => {
              setType(e.target.value as FormType);
              setTitle("");
              setErrors({});
            }}
          >
            <option value="credit">Credit Request</option>
            <option value="demo">Demo Request</option>
          </FormSelect>
        </Field>
        <Field label="Customer" error={errors.customer_id?.[0]}>
          <FormSelect
            value={customer}
            disabled={!!customerId}
            onChange={(e) => setCustomer(e.target.value)}
            required
          >
            <option value="">Select a customer</option>
            {options.customers.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </FormSelect>
        </Field>
        <Field
          label={type === "credit" ? "Invoice number" : "Event name"}
          error={(errors.title || errors.invoice_number || errors.event_name)?.[0]}
        >
          <FormInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={type === "credit" ? 100 : 255}
            required
          />
        </Field>
        {type === "demo" && (
          <Field label="Requested event date" error={errors.event_date?.[0]}>
            <FormInput
              type="date"
              value={date}
              min={today()}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </Field>
        )}
        <Field label="Description" error={errors.description?.[0]}>
          <textarea
            className={inputClass}
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={10000}
            required
          />
        </Field>
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <p className="text-sm text-gray-500">
        Submitted requests start as Pending.
        {type === "credit" && " Submitting a request does not issue a credit."}
      </p>
      <div className="flex justify-end gap-3">
        <button type="button" className={secondaryClass} disabled={saving} onClick={onClose}>
          Cancel
        </button>
        <button className={buttonClass} disabled={saving}>
          {saving ? "Submitting…" : `Submit ${type === "credit" ? "credit" : "demo"} request`}
        </button>
      </div>
    </form>
  );
}
